import React from "react";
import { Container, Form, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon } from "@simplex/sx-icons";
import {
  HiddenLines3d,
  Render3dEngine,
  selectAxisLabelOffset3d,
  selectAxisOffset3d,
  selectAxisSize3d,
  selectCardboardDistance3d,
  selectGeometryDebug3d,
  selectDebugExtrusion3d,
  selectDebugExtrusionNormals3d,
  selectDebugExtrusionNormalsCenter3d,
  selectDebugExtrusionNormalsRadius3d,
  selectDrawFaces3d,
  selectShadeFaces3d,
  selectLightYaw3d,
  selectLightPitch3d,
  selectShowLightDirection3d,
  selectShadeLighterTimberPercent3d,
  selectShadeLighterPercent3d,
  selectShadeDarkerPercent3d,
  selectHiddenLines3d,
  selectHiddenLineGrey3d,
  selectOffsetLoadsFromColumn3d,
  selectRender3dEngine,
  selectRightHand3d,
  selectResultBoardClearance3d,
  selectResultBoardHeaderOffsetsLeft3d,
  selectResultBoardHeaderOffsetsRight3d,
  selectResultBoardGraphAmplitude3d,
  selectResultBoardLaneWidth3d,
  selectShow3dCardboard,
  selectShow3dLoadsOnOppositeSide,
  selectShow3dReinforcement,
  selectStartCardboardsAtOuterCenterLine3d,
  selectScale3dX,
  selectScale3dYZ,
  selectLoadColumnSpacing3d,
  setAxisLabelOffset3d,
  setAxisOffset3d,
  setAxisSize3d,
  setCardboardDistance3d,
  setGeometryDebug3d,
  setDebugExtrusion3d,
  setDebugExtrusionNormals3d,
  setDebugExtrusionNormalsCenter3d,
  setDebugExtrusionNormalsRadius3d,
  setDrawFaces3d,
  setShadeFaces3d,
  setLightYaw3d,
  setLightPitch3d,
  setShowLightDirection3d,
  setShadeLighterTimberPercent3d,
  setShadeLighterPercent3d,
  setShadeDarkerPercent3d,
  setHiddenLines3d,
  setHiddenLineGrey3d,
  setOffsetLoadsFromColumn3d,
  setRender3dEngine,
  setRightHand3d,
  setResultBoardClearance3d,
  setResultBoardHeaderOffset3d,
  setResultBoardGraphAmplitude3d,
  setResultBoardLaneWidth3d,
  setScale3dX,
  setScale3dYZ,
  setShow3dCardboard,
  setShow3dLoadsOnOppositeSide,
  setShow3dReinforcement,
  setStartCardboardsAtOuterCenterLine3d,
  setLoadColumnSpacing3d,
} from "../features/ui/uiSlice";

// the result board lanes, in lane order
const HEADER_LANE_LABELS = ["N", "M / V", "d"] as const;

interface NumberSettingProps {
  id: string;
  label: string;
  value: number;
  min: number;
  step: number;
  xs?: number;
  onChange: (value: number) => void;
}

const NumberSetting = ({ id, label, value, min, step, xs = 6, onChange }: NumberSettingProps) => (
  <Col xs={xs}>
    <Form.Group className="mb-1">
      <Form.Label className="editfield-label mb-0">{label}</Form.Label>
      <Form.Control
        id={id}
        type="number"
        min={min}
        step={step}
        value={value}
        onChange={(event) => {
          const next = parseFloat(event.target.value);
          if (Number.isFinite(next) && next >= min) onChange(next);
        }}
      />
    </Form.Group>
  </Col>
);

interface RangeSettingProps {
  id: string;
  label: string;
  value: number;
  max?: number;
  unit?: string;
  /** Icons at the low and high ends of the slider, showing which way it turns. */
  ends?: [React.ReactNode, React.ReactNode];
  onChange: (value: number) => void;
}

const RangeSetting = ({ id, label, value, max = 100, unit = "%", ends, onChange }: RangeSettingProps) => (
  <Col xs={6}>
    <Form.Group className="mb-1">
      <Form.Label className="editfield-label mb-0">
        {label}: {value}
        {unit}
      </Form.Label>
      <div className="d-flex align-items-center gap-1">
        {ends?.[0]}
        <Form.Range
          id={id}
          min={0}
          max={max}
          step={1}
          value={value}
          onChange={(event) => onChange(Number(event.target.value))}
        />
        {ends?.[1]}
      </div>
    </Form.Group>
  </Col>
);

export const SX3DViewSettings = () => {
  const dispatch = useDispatch();
  const render3dEngine = useSelector(selectRender3dEngine);
  const rightHand3d = useSelector(selectRightHand3d);
  const scale3dX = useSelector(selectScale3dX);
  const scale3dYZ = useSelector(selectScale3dYZ);
  const cardboardDistance3d = useSelector(selectCardboardDistance3d);
  const resultBoardLaneWidth3d = useSelector(selectResultBoardLaneWidth3d);
  const resultBoardGraphAmplitude3d = useSelector(selectResultBoardGraphAmplitude3d);
  const resultBoardClearance3d = useSelector(selectResultBoardClearance3d);
  const resultBoardHeaderOffsetsLeft3d = useSelector(selectResultBoardHeaderOffsetsLeft3d);
  const resultBoardHeaderOffsetsRight3d = useSelector(selectResultBoardHeaderOffsetsRight3d);
  const show3dCardboard = useSelector(selectShow3dCardboard);
  const startCardboardsAtOuterCenterLine3d = useSelector(selectStartCardboardsAtOuterCenterLine3d);
  const show3dLoadsOnOppositeSide = useSelector(selectShow3dLoadsOnOppositeSide);
  const offsetLoadsFromColumn3d = useSelector(selectOffsetLoadsFromColumn3d);
  const loadColumnSpacing3d = useSelector(selectLoadColumnSpacing3d);
  const show3dReinforcement = useSelector(selectShow3dReinforcement);
  const axisSize3d = useSelector(selectAxisSize3d);
  const axisOffset3d = useSelector(selectAxisOffset3d);
  const axisLabelOffset3d = useSelector(selectAxisLabelOffset3d);
  const geometryDebug3d = useSelector(selectGeometryDebug3d);
  const debugExtrusion3d = useSelector(selectDebugExtrusion3d);
  const debugExtrusionNormals3d = useSelector(selectDebugExtrusionNormals3d);
  const debugExtrusionNormalsCenter3d = useSelector(selectDebugExtrusionNormalsCenter3d);
  const debugExtrusionNormalsRadius3d = useSelector(selectDebugExtrusionNormalsRadius3d);
  const drawFaces3d = useSelector(selectDrawFaces3d);
  const shadeFaces3d = useSelector(selectShadeFaces3d);
  const lightYaw3d = useSelector(selectLightYaw3d);
  const lightPitch3d = useSelector(selectLightPitch3d);
  const showLightDirection3d = useSelector(selectShowLightDirection3d);
  const shadeLighterTimberPercent3d = useSelector(selectShadeLighterTimberPercent3d);
  const shadeLighterPercent3d = useSelector(selectShadeLighterPercent3d);
  const shadeDarkerPercent3d = useSelector(selectShadeDarkerPercent3d);
  const hiddenLines3d = useSelector(selectHiddenLines3d);
  const hiddenLineGrey3d = useSelector(selectHiddenLineGrey3d);

  return (
    <Container fluid id="sx-table-container" className="px-2">
      <Row className="container-row align-items-center mb-1">
        <Col xs={6} className="editfield-label pe-1">
          Render engine
        </Col>
        <Col xs={6} className="ps-1" style={{ height: 32 }}>
          <Form.Select
            id="render3dEngineSelect"
            value={render3dEngine}
            onChange={(event) => dispatch(setRender3dEngine(event.target.value as Render3dEngine))}
          >
            <option value="svg">SVG</option>
            <option value="three">Three.js</option>
          </Form.Select>
        </Col>
      </Row>
      <Row className="container-row mb-2">
        <Col>
          <Form.Check
            type="switch"
            className="sx-switch"
            id="rightHand3dInput"
            label="Right hand"
            checked={rightHand3d}
            onChange={(event) => dispatch(setRightHand3d(event.target.checked))}
          />
        </Col>
      </Row>
      <Row className="g-1 mb-2">
        <NumberSetting
          id="scale3dXInput"
          label="X-axis scale"
          value={scale3dX}
          min={0.01}
          step={0.1}
          onChange={(value) => dispatch(setScale3dX(value))}
        />
        <NumberSetting
          id="scale3dYZInput"
          label="YZ-axis scale"
          value={scale3dYZ}
          min={0.01}
          step={0.1}
          onChange={(value) => dispatch(setScale3dYZ(value))}
        />
      </Row>
      <Row className="container-row mb-2">
        <Col>
          <Form.Check
            type="switch"
            className="sx-switch"
            id="geometryDebug3dInput"
            label="Geometry debug (hide loads & output)"
            checked={geometryDebug3d}
            onChange={(event) => dispatch(setGeometryDebug3d(event.target.checked))}
          />
          <Form.Check
            type="switch"
            className="sx-switch mt-2"
            id="debugExtrusion3dInput"
            label="Debug extrusion edges"
            checked={debugExtrusion3d}
            onChange={(event) => dispatch(setDebugExtrusion3d(event.target.checked))}
          />
          <Form.Check
            type="switch"
            className="sx-switch mt-2"
            id="debugExtrusionNormals3dInput"
            label="Debug extrusion face normals"
            checked={debugExtrusionNormals3d}
            onChange={(event) => dispatch(setDebugExtrusionNormals3d(event.target.checked))}
          />
          <Form.Check
            type="switch"
            className="sx-switch mt-2"
            id="drawFaces3dInput"
            label="Draw faces"
            checked={drawFaces3d}
            onChange={(event) => dispatch(setDrawFaces3d(event.target.checked))}
          />
          <Form.Check
            type="switch"
            className="sx-switch mt-2"
            id="shadeFaces3dInput"
            label="Shade faces"
            checked={shadeFaces3d}
            onChange={(event) => dispatch(setShadeFaces3d(event.target.checked))}
          />
          <Form.Check
            type="switch"
            className="sx-switch mt-2"
            id="showLightDirection3dInput"
            label="Show light direction"
            checked={showLightDirection3d}
            onChange={(event) => dispatch(setShowLightDirection3d(event.target.checked))}
          />
        </Col>
      </Row>
      <Row className="g-1 mb-2">
        <RangeSetting
          id="lightYaw3dInput"
          label="Light yaw"
          value={lightYaw3d}
          max={360}
          unit="°"
          ends={[<ChevronLeftIcon key="left" size={12} />, <ChevronRightIcon key="right" size={12} />]}
          onChange={(value) => dispatch(setLightYaw3d(value))}
        />
        <RangeSetting
          id="lightPitch3dInput"
          label="Light pitch"
          value={lightPitch3d}
          max={360}
          unit="°"
          ends={[<ChevronDownIcon key="down" size={12} />, <ChevronUpIcon key="up" size={12} />]}
          onChange={(value) => dispatch(setLightPitch3d(value))}
        />
      </Row>
      <Row className="g-1 mb-2">
        <RangeSetting
          id="shadeLighterTimberPercent3dInput"
          label="Light color: timber"
          value={shadeLighterTimberPercent3d}
          onChange={(value) => dispatch(setShadeLighterTimberPercent3d(value))}
        />
        <RangeSetting
          id="shadeLighterPercent3dInput"
          label="Light color: other materials"
          value={shadeLighterPercent3d}
          onChange={(value) => dispatch(setShadeLighterPercent3d(value))}
        />
      </Row>
      <Row className="g-1 mb-2">
        <RangeSetting
          id="shadeDarkerPercent3dInput"
          label="Dark color: darker"
          value={shadeDarkerPercent3d}
          onChange={(value) => dispatch(setShadeDarkerPercent3d(value))}
        />
      </Row>
      <Row className="container-row align-items-center mb-2">
        <Col xs={6} className="editfield-label pe-1">
          Hidden lines
        </Col>
        <Col xs={6} className="ps-1" style={{ height: 32 }}>
          <Form.Select
            id="hiddenLines3dSelect"
            value={hiddenLines3d}
            onChange={(event) => dispatch(setHiddenLines3d(event.target.value as HiddenLines3d))}
          >
            <option value="hide">Don&apos;t draw</option>
            <option value="dashed">Dashed</option>
            <option value="grey">Grey</option>
            <option value="dashedGrey">Dashed grey</option>
            <option value="solid">Normal lines</option>
          </Form.Select>
        </Col>
      </Row>
      {(hiddenLines3d === "grey" || hiddenLines3d === "dashedGrey") && (
        <Row className="g-1 mb-2">
          <RangeSetting
            id="hiddenLineGrey3dInput"
            label="Hidden line grey"
            value={hiddenLineGrey3d}
            onChange={(value) => dispatch(setHiddenLineGrey3d(value))}
          />
        </Row>
      )}
      {debugExtrusionNormals3d && (
        <Row className="g-1 mb-2">
          <RangeSetting
            id="debugExtrusionNormalsCenter3dInput"
            label="Normal range center"
            value={debugExtrusionNormalsCenter3d}
            onChange={(value) => dispatch(setDebugExtrusionNormalsCenter3d(value))}
          />
          <RangeSetting
            id="debugExtrusionNormalsRadius3dInput"
            label="Normal range radius"
            value={debugExtrusionNormalsRadius3d}
            onChange={(value) => dispatch(setDebugExtrusionNormalsRadius3d(value))}
          />
        </Row>
      )}

      <Row className="container-row mb-1">
        <Col className="editfield-label fw-bolder ps-2">Cardboards</Col>
      </Row>
      <Row className="g-1 mb-1">
        <NumberSetting
          id="cardboardDistance3dInput"
          label="Distance"
          value={cardboardDistance3d}
          min={0}
          step={0.05}
          onChange={(value) => dispatch(setCardboardDistance3d(value))}
        />
        <NumberSetting
          id="resultBoardLaneWidth3dInput"
          label="Lane width"
          value={resultBoardLaneWidth3d}
          min={0.01}
          step={0.05}
          onChange={(value) => dispatch(setResultBoardLaneWidth3d(value))}
        />
        <NumberSetting
          id="resultBoardGraphAmplitude3dInput"
          label="Graph amplitude"
          value={resultBoardGraphAmplitude3d}
          min={0.01}
          step={0.05}
          onChange={(value) => dispatch(setResultBoardGraphAmplitude3d(value))}
        />
        <NumberSetting
          id="resultBoardClearance3dInput"
          label="Overlap clearance"
          value={resultBoardClearance3d}
          min={0}
          step={0.05}
          onChange={(value) => dispatch(setResultBoardClearance3d(value))}
        />
      </Row>
      {(
        [
          ["left", "Header x offset, left board (m)", resultBoardHeaderOffsetsLeft3d],
          ["right", "Header x offset, right board (m)", resultBoardHeaderOffsetsRight3d],
        ] as const
      ).map(([side, title, offsets]) => (
        <React.Fragment key={side}>
          <Row className="container-row mb-0">
            <Col className="editfield-label ps-2">{title}</Col>
          </Row>
          <Row className="g-1 mb-1">
            {HEADER_LANE_LABELS.map((laneLabel, lane) => (
              <NumberSetting
                key={laneLabel}
                id={`resultBoardHeaderOffset3d-${side}-${lane}`}
                label={laneLabel}
                value={offsets[lane]}
                min={-100}
                step={0.01}
                xs={4}
                onChange={(value) => dispatch(setResultBoardHeaderOffset3d({ side, lane, value }))}
              />
            ))}
          </Row>
        </React.Fragment>
      ))}
      <Row className="container-row mb-2">
        <Col>
          <Form.Check
            type="switch"
            className="sx-switch"
            id="startCardboardsAtOuterCenterLine3dInput"
            label="Start at column center"
            checked={startCardboardsAtOuterCenterLine3d}
            onChange={(event) => dispatch(setStartCardboardsAtOuterCenterLine3d(event.target.checked))}
          />
          <Form.Check
            type="switch"
            className="sx-switch mt-2"
            id="show3dCardboardInput"
            label="Show cardboards"
            checked={show3dCardboard}
            onChange={(event) => dispatch(setShow3dCardboard(event.target.checked))}
          />
        </Col>
      </Row>

      <Row className="container-row mb-1">
        <Col className="editfield-label fw-bolder ps-2">Loads</Col>
      </Row>
      <Row className="container-row mb-1">
        <Col>
          <Form.Check
            type="switch"
            className="sx-switch"
            id="show3dLoadsOnOppositeSideInput"
            label="Show loads on opposite side"
            checked={show3dLoadsOnOppositeSide}
            onChange={(event) => dispatch(setShow3dLoadsOnOppositeSide(event.target.checked))}
          />
          <Form.Check
            type="switch"
            className="sx-switch mt-2"
            id="offsetLoadsFromColumn3dInput"
            label="Offset loads from column"
            checked={offsetLoadsFromColumn3d}
            onChange={(event) => dispatch(setOffsetLoadsFromColumn3d(event.target.checked))}
          />
        </Col>
      </Row>
      {offsetLoadsFromColumn3d && (
        <Row className="g-1 mb-2">
          <NumberSetting
            id="loadColumnSpacing3dInput"
            label="Load-to-column spacing"
            value={loadColumnSpacing3d}
            min={0}
            step={0.05}
            onChange={(value) => dispatch(setLoadColumnSpacing3d(value))}
          />
        </Row>
      )}

      <Row className="container-row mb-1">
        <Col className="editfield-label fw-bolder ps-2">Axis</Col>
      </Row>
      <Row className="g-1 mb-1">
        <NumberSetting
          id="axisSize3dInput"
          label="Size"
          value={axisSize3d}
          min={0}
          step={0.05}
          onChange={(value) => dispatch(setAxisSize3d(value))}
        />
        <NumberSetting
          id="axisOffset3dInput"
          label="Offset"
          value={axisOffset3d}
          min={0}
          step={0.025}
          onChange={(value) => dispatch(setAxisOffset3d(value))}
        />
        <NumberSetting
          id="axisLabelOffset3dInput"
          label="Label offset"
          value={axisLabelOffset3d}
          min={0}
          step={1}
          onChange={(value) => dispatch(setAxisLabelOffset3d(value))}
        />
      </Row>
      <Row className="container-row">
        <Col>
          <Form.Check
            type="switch"
            className="sx-switch"
            id="show3dReinforcementInput"
            label="Show reinforcement"
            checked={show3dReinforcement}
            onChange={(event) => dispatch(setShow3dReinforcement(event.target.checked))}
          />
        </Col>
      </Row>
    </Container>
  );
};
